import {
    IAccountInfoResponseData,
    IAccountSubscription,
    IMdrProduct,
    TAccountServiceUserLoginSuccess,
    TPolicyExpirationStatus,
    TSubscriptionType,
} from '@/core/modules/account/account'
import {
    AccountInfoByIdResponseData,
    SubscriptionResponseData,
} from '@atbay/account-service-node-sdk/lib/types'

type TUserSuccessLoginResponse = {
    users: TCurrentUser
    accounts: Record<string, unknown>[]
    expired_accounts?: string[]
}

export function userLoginSuccessTransformer(data: TUserSuccessLoginResponse) {
    const currentUser: TCurrentUser = { ...data.users }

    const firstAccountId = data.accounts[0]?.id as string

    const accounts = data.accounts.reduce((final, account) => {
        final[account.id as string] = {
            security_contact: account?.account_owner_name as string,
            security_contact_email: account?.account_owner_email as string,
            company_name: account?.company_name as string,
            industry: account?.industry as string,
            date_created: account.created_at,
            domains: [], // todo missing field BE
            is_active: account?.is_active,
            subscription: account?.subscription,
            company_id: account?.company_id,
            industry_score: account?.industry_score,
            id: account?.id as string,
            enforce_mfa: account.enforce_mfa,
        }

        return final
    }, {})

    return {
        user: currentUser,
        account: firstAccountId,
        accounts: accounts,
        expiredAccountsNames: data.expired_accounts,
        onlyExpiredAccounts: false,
    } as TAccountServiceUserLoginSuccess
}

const subscriptionTransformer = (
    subscription: SubscriptionResponseData
): IAccountSubscription => ({
    id: subscription.id,
    subscriptionType: subscription.subscription_type as TSubscriptionType,
    effectiveDate: subscription.effective_date,
    expirationDate: subscription.expiration_date ?? '',
    expirationStatus:
        (subscription.expiration_status as TPolicyExpirationStatus) ?? 'valid',
    countDownInDays: subscription.count_down_in_days ?? 0,
    britecorePolicyNumber: subscription.britecore_policy_number ?? null,
    fdExtendedLimit: subscription.fd_extended_limit,
    mdrExtendedLimit: subscription.mdr_extended_limit,
    policyRequestId:
        subscription.policy_request_id != null
            ? String(subscription.policy_request_id)
            : null,
})

export const accountInfoTransformer = (
    data: AccountInfoByIdResponseData
): IAccountInfoResponseData => ({
    accountId: data.account_id,
    accountOwnerEmail: data.account_owner_email ?? null,
    accountOwnerName: data.account_owner_name ?? null,
    britecorePolicyNumber: data.britecore_policy_number ?? null,
    companyName: data.company_name,
    countDownInDays: data.count_down_in_days,
    dateCreated: data.date_created,
    fdExtendedLimit: data.fd_extended_limit,
    industry: data.industry,
    isMdr: data.is_mdr ?? false,
    mdrExtendedLimit: data.mdr_extended_limit,
    mdrProducts: data.mdr_products || [],
    policyExpirationDate: data.policy_expiration_date,
    policyExpirationStatus: data.policy_expiration_status,
    policyRequestId: data.policy_request_id || null,
    subscriptions: (data.subscriptions ?? []).map(subscriptionTransformer),
})

export const transformTeam = (users: TResponseTeamMember[]) => {
    // todo : improve sorting of users by email/name

    const result: Record<string, TTeamMember> | [] = users
        .map((member) => {
            return {
                user_id: member.id,
                // todo : refactor name, first_name, last_name and sorting by name
                name:
                    member.first_name || member.last_name
                        ? `${member.first_name} ${member.last_name}`
                        : member.email.value,
                first_name: member.first_name || '',
                last_name: member.last_name || '',
                email: member.email.value,
                role: member.job_title || '',
                is_active: member.is_active,
                send_alerts: member.enable_email_notification ?? false,
                mfa_active: member.mfa_active || false,
            }
        })
        .reduce(
            (acc, member) => {
                acc[member.user_id] = member
                return acc
            },
            {} as Record<string, TTeamMember>
        )

    return result
}
